/**
 * 長野県の背景ツアー「長野めぐり」。10か所、それぞれに3つの豆知識。
 *
 * 書いてはいけないこと。
 * - 善光寺は公式が「『善光寺』の名称ならびに諸堂の肖像の商標権は宗教法人善光寺に帰属」
 *   「境内での撮影、写真・イラスト使用には必ず申請が必要」と明記しているので入れていない。
 * - 軽井沢の白糸の滝は静岡の白糸ノ滝と名前が重なるので入れていない。
 * - 戸隠は奥社の参道に鳥居と朱塗りの随神門がある。杉並木だけを描く。
 * - 地獄谷野猿公苑は、人がサルに近づいて触る、餌をやる、目を合わせるという絵が公式のルールに反する。
 *   人を描かず、湯に浸かるサルだけにする。自撮り棒もドローンも描かない。
 * - 千畳敷カールのロープウェイのゴンドラと駅舎は企業の施設なので描かない。
 * - 姨捨は棄老説話と結びついた土地なので、伝説には触れず「田毎の月」と名勝の側面で扱う。
 *   棚田はいまも使われている私有の農地である。
 * - 上高地の大正池は「立ち枯れの木々も減少」と出典にあるので、立ち枯れを主役にしすぎない。
 * - 奈良井宿は「日本最長」とは書かない。「約1,000メートルにわたる宿場集落」が公式の表現。
 * - 高遠の「天下第一の桜」という呼称は日本語の原文を確認できなかったので使わない。
 */

import type { StageTour } from '../types';

export const naganoSceneryTour: StageTour = {
  scenes: [
    {
      id: 'takato',
      name: '高遠城址公園',
      area: '伊那市',
      image: '/assets/scenery/nagano/scenes/01_takato.webp',
      hotspots: [
        {
          id: 'takato-shiseki',
          x: 0.25,
          y: 0.7,
          title: '昭和48年に国の史跡',
          body: '高遠城址は昭和48年（1973年）に国の史跡に指定された。',
          source: 'https://www.inacity.jp/kurashi/shogaigakushu_bunka/bunkazai/takatojyoseki.html',
          mapQuery: '高遠城址公園',
        },
        {
          id: 'takato-species',
          x: 0.55,
          y: 0.35,
          title: 'タカトオコヒガンザクラ',
          body: '城跡の桜は「タカトオコヒガンザクラ」という固有種で、やや小ぶりで赤みが強い花が特徴。龍のように曲がりくねった幹も見ごたえがある。',
          source: 'https://www.inacity.jp/kurashi/shogaigakushu_bunka/bunkazai/takatojyoseki.html',
          mapQuery: '伊那市 高遠',
        },
        {
          id: 'takato-count',
          x: 0.8,
          y: 0.65,
          title: '1500本、古木は約10本',
          body: '「高遠のコヒガンザクラ樹林」として昭和35年に長野県の天然記念物に指定された。現在、城跡の周辺に1,500本が生育しており、約140年前の明治初期に植えられた古木が約10本、50年以上のものが約1,000本ある。',
          source: 'https://www.inacity.jp/kurashi/shogaigakushu_bunka/bunkazai/takatojyoseki.html',
          mapQuery: '高遠城址公園 桜',
        },
      ],
    },
    {
      id: 'obasute',
      name: '姨捨の棚田',
      area: '千曲市',
      image: '/assets/scenery/nagano/scenes/02_obasute.webp',
      hotspots: [
        {
          id: 'obasute-size',
          x: 0.28,
          y: 0.6,
          title: '75ヘクタール、約1800枚',
          body: '棚田の規模は75ヘクタール、約1,800枚。うち重要文化的景観の選定区域は64.3ヘクタールで、さらにその中の約40ヘクタールに約1,500枚の棚田がある。',
          source: 'https://www.city.chikuma.lg.jp/soshiki/norin/kanko/3/1659.html',
          mapQuery: '姨捨の棚田',
        },
        {
          id: 'obasute-slope',
          x: 0.55,
          y: 0.78,
          title: '傾斜は7分の1',
          body: '千曲川の左岸、三峯山の北東側、山腹斜面の標高460メートルから550メートルの範囲に広がる。一般に傾斜が20分の1以上の斜面にある田んぼを棚田と呼ぶが、姨捨の棚田は傾斜7分の1の斜面にある。',
          source: 'https://www.city.chikuma.lg.jp/soshiki/norin/kanko/3/1659.html',
          mapQuery: '千曲市 姨捨',
        },
        {
          id: 'obasute-tagoto',
          x: 0.78,
          y: 0.3,
          title: '「田毎の月」の初出',
          body: '棚田として全国で初めて国の名勝に指定されたのは平成11年5月10日。平成22年に重要文化的景観、令和2年に日本遺産にも認定された。「田毎の月」は1578年に著された狂言本『木賊』に登場するのが最初で、これが棚田についての最初の記録とされる。',
          source: 'https://www.city.chikuma.lg.jp/soshiki/norin/kanko/3/1659.html',
          mapQuery: '姨捨 田毎の月',
        },
      ],
    },
    {
      id: 'taishoike',
      name: '上高地 大正池',
      area: '松本市',
      image: '/assets/scenery/nagano/scenes/03_taishoike.webp',
      hotspots: [
        {
          id: 'taishoike-origin',
          x: 0.28,
          y: 0.65,
          title: '大正4年の噴火でできた',
          body: '焼岳は大正4年（1915年）に噴火し、噴出した熔岩や泥流によって梓川がせき止められて大正池が出現した。',
          source: 'https://www.kamikochi.or.jp/learn/spots/11/',
          mapQuery: '大正池 上高地',
        },
        {
          id: 'taishoike-shrink',
          x: 0.55,
          y: 0.35,
          title: '面積は半分以下に',
          body: '当初は広大で「梓湖」と呼ばれたが、のちに大正池と呼ばれるようになった。100年以上経った現在、池の面積は半分以下に縮小し、立ち枯れの木々も減っている。',
          source: 'https://www.kamikochi.or.jp/learn/spots/11/',
          mapQuery: '上高地 大正池',
        },
        {
          id: 'taishoike-dredge',
          x: 0.8,
          y: 0.78,
          title: '11月に浚渫が行われる',
          body: '11月には、大正池に流れ込む土砂を取り除く浚渫の作業が行われている。',
          source: 'https://www.kamikochi.or.jp/learn/spots/11/',
          mapQuery: '松本市 上高地',
        },
      ],
    },
    {
      id: 'kappabashi',
      name: '上高地 河童橋',
      area: '松本市',
      image: '/assets/scenery/nagano/scenes/04_kappabashi.webp',
      hotspots: [
        {
          id: 'kappabashi-symbol',
          x: 0.28,
          y: 0.35,
          title: '上高地のシンボル',
          body: '上高地観光旅館組合は「上高地のシンボルともいえる河童橋。人工物でありながら、その姿は大自然と融合し上高地に欠かせない風格のある存在となっています」と記している。',
          source: 'https://www.kamikochi.or.jp/learn/spots/1319/',
          mapQuery: '河童橋 上高地',
        },
        {
          id: 'kappabashi-fifth',
          x: 0.55,
          y: 0.6,
          title: 'いまの橋は5代目',
          body: '当初は跳ね橋で、明治43年（1910年）に吊り橋となった。その後も架け替えが続き、現在の橋は平成9年（1997年）に架け替えられた5代目の吊り橋である。',
          source: 'https://www.kamikochi.or.jp/learn/spots/1319/',
          mapQuery: '上高地 梓川',
        },
        {
          id: 'kappabashi-view',
          x: 0.8,
          y: 0.3,
          title: '振り返ると焼岳',
          body: '橋からは雄々しくそびえる穂高連峰や岳沢、清冽なる梓川の流れが見え、川辺のケショウヤナギやカラマツも見られる。振り返ると、時折噴煙たなびく焼岳が展望できる。',
          source: 'https://www.kamikochi.or.jp/learn/spots/1319/',
          mapQuery: '穂高連峰',
        },
      ],
    },
    {
      id: 'senjojiki',
      name: '千畳敷カール',
      area: '駒ヶ根市',
      image: '/assets/scenery/nagano/scenes/05_senjojiki.webp',
      hotspots: [
        {
          id: 'senjojiki-height',
          x: 0.28,
          y: 0.35,
          title: '標高2612メートル',
          body: '標高2,612メートルの千畳敷駅でロープウェイを降りると、目の前に千畳敷カールの絶景が広がる。',
          source: 'https://www.kankou-komagane.com/alps/senjojiki',
          mapQuery: '千畳敷カール',
        },
        {
          id: 'senjojiki-glacier',
          x: 0.55,
          y: 0.65,
          title: '氷河でつくられた地形',
          body: '約2万年前の氷河期に発達した氷河でつくられたといわれる地形で、高山植物の宝庫として知られている。カール内の遊歩道は1周約45分で周遊できる。',
          source: 'https://www.kankou-komagane.com/alps/senjojiki',
          mapQuery: '駒ヶ根市 千畳敷',
        },
        {
          id: 'senjojiki-ropeway',
          x: 0.8,
          y: 0.78,
          title: '高低差950メートル',
          body: '中央アルプス駒ヶ岳ロープウェイは高低差950メートルで日本一となっており、終点の標高2,612メートルの駅は日本一標高が高い場所にある駅である。',
          source: 'https://www.kankou-komagane.com/alps/senjojiki',
          mapQuery: '中央アルプス 千畳敷駅',
        },
      ],
    },
    {
      id: 'togakushi-sugi',
      name: '戸隠 奥社の杉並木',
      area: '長野市',
      image: '/assets/scenery/nagano/scenes/06_togakushi.webp',
      hotspots: [
        {
          id: 'togakushi-sugi-history',
          x: 0.25,
          y: 0.35,
          title: '二千年余りの歴史',
          body: '戸隠神社は、霊山である戸隠山の麓を中心に創建された、二千年余りに及ぶ歴史を刻む神社。奥社、中社、宝光社、九頭龍社、火之御子社の五社で構成される。',
          source: 'https://www.togakushi-jinja.jp/about',
          mapQuery: '戸隠神社 奥社',
        },
        {
          id: 'togakushi-sugi-cedar',
          x: 0.55,
          y: 0.6,
          title: '樹齢400年を超える杉',
          body: '奥社の参道には樹齢約400年を超える杉並木があり、随神門から約500メートル続く。天然記念物にも指定されている。',
          source: 'https://www.togakushi-jinja.jp/about',
          mapQuery: '戸隠神社 杉並木',
        },
        {
          id: 'togakushi-sugi-faith',
          x: 0.8,
          y: 0.78,
          title: '修験道と結びついた山',
          body: '天台密教が伝わると神仏習合の顕光寺が創建され、戸隠信仰は修験道とも習合した。明治初年の神仏分離令により仏教的なものは一掃され、神社神道として歩むことになった。',
          source: 'https://www.togakushi-jinja.jp/about',
          mapQuery: '長野市 戸隠',
        },
      ],
    },
    {
      id: 'nezame',
      name: '寝覚の床',
      area: '上松町',
      image: '/assets/scenery/nagano/scenes/07_nezame.webp',
      hotspots: [
        {
          id: 'nezame-meisho',
          x: 0.28,
          y: 0.32,
          title: '大正12年に名勝',
          body: '種別は名勝で、指定年月日は1923年（大正12年）3月7日。所在地は長野県木曽郡上松町。',
          source: 'https://kunishitei.bunka.go.jp/heritage/detail/401/1162',
          mapQuery: '寝覚の床',
        },
        {
          id: 'nezame-granite',
          x: 0.55,
          y: 0.62,
          title: '花崗岩の峡谷',
          body: '花崗岩で形成された木曽川の峡谷で、河幅が狭く両岸がほぼ垂直に立ち、深い淵をなす特徴的な花崗岩の峡谷である。',
          source: 'https://kunishitei.bunka.go.jp/heritage/detail/401/1162',
          mapQuery: '上松町 木曽川',
        },
        {
          id: 'nezame-rocks',
          x: 0.8,
          y: 0.8,
          title: '名のついた岩',
          body: '両岸には階段状の地形があり、水の浸食の痕跡が見られ、岩石には多くの甌穴がある。釜石、獅子岩、象岩、烏帽子岩、屏風岩など独特の名前を持つ岩がある。',
          source: 'https://kunishitei.bunka.go.jp/heritage/detail/401/1162',
          mapQuery: '寝覚の床 上松町',
        },
      ],
    },
    {
      id: 'naraijuku',
      name: '奈良井宿',
      area: '塩尻市',
      image: '/assets/scenery/nagano/scenes/08_naraijuku.webp',
      hotspots: [
        {
          id: 'naraijuku-selected',
          x: 0.28,
          y: 0.35,
          title: '昭和53年に選定、約1000メートル',
          body: '重要伝統的建造物群保存地区としての選定は1978年（昭和53年5月31日）。面積は約17.6ヘクタールで、約1,000メートルにわたる宿場集落である。',
          source: 'https://www.city.shiojiri.lg.jp/soshiki/36/3735.html',
          mapQuery: '奈良井宿',
        },
        {
          id: 'naraijuku-dashibari',
          x: 0.55,
          y: 0.6,
          title: '出梁造り',
          body: '建築は切妻・平入、中二階建で、低い二階の前面を張り出して縁としている。地域の大きな特徴は「出梁（だしばり）造り」である。',
          source: 'https://www.city.shiojiri.lg.jp/soshiki/36/3735.html',
          mapQuery: '塩尻市 奈良井',
        },
        {
          id: 'naraijuku-roof',
          x: 0.8,
          y: 0.78,
          title: '深い軒になる理由',
          body: '屋根は勾配をゆるく前面に出していることから、深い軒となっている。もともとは板葺石置屋根だったが、近年は鉄板葺が主流になっている。',
          source: 'https://www.city.shiojiri.lg.jp/soshiki/36/3735.html',
          mapQuery: '奈良井宿 中山道',
        },
      ],
    },
    {
      id: 'matsumoto-jo',
      name: '国宝 松本城',
      area: '松本市',
      image: '/assets/scenery/nagano/scenes/09_matsumoto.webp',
      hotspots: [
        {
          id: 'matsumoto-jo-oldest',
          x: 0.28,
          y: 0.35,
          title: '五重六階の天守では日本最古とされる',
          body: '松本城の公式サイトは、現存する天守12城のうち、五重六階の天守としては日本最古と紹介している。大天守は、豊臣秀吉の家臣、石川数正・康長父子により創建された。',
          source: 'https://www.matsumoto-castle.jp/about/tower',
          mapQuery: '松本城',
        },
        {
          id: 'matsumoto-jo-five',
          x: 0.55,
          y: 0.6,
          title: '五棟でひとつの天守群',
          body: '天守群は大天守、乾小天守、渡櫓、辰巳附櫓、月見櫓の五棟で形成されている。大天守・乾小天守・渡櫓は文禄2年から3年（1593〜94年）にかけて築造され、辰巳附櫓と月見櫓は江戸時代初期のもの。',
          source: 'https://www.matsumoto-castle.jp/about/tower',
          mapQuery: '松本市 松本城',
        },
        {
          id: 'matsumoto-jo-kokuho',
          x: 0.8,
          y: 0.3,
          title: '城郭では姫路に次ぐ指定',
          body: '昭和11年（1936年）に国宝に指定され、戦後は昭和25年に制定された文化財保護法によって、昭和27年に改めて国宝の指定を受けた。城郭では姫路に次いでの指定だった。',
          source: 'https://www.matsumoto-castle.jp/value/national-treasure',
          mapQuery: '国宝松本城',
        },
      ],
    },
    {
      id: 'jigokudani',
      name: '地獄谷野猿公苑',
      area: '山ノ内町',
      image: '/assets/scenery/nagano/scenes/10_jigokudani.webp',
      hotspots: [
        {
          id: 'jigokudani-cold',
          x: 0.28,
          y: 0.32,
          title: '標高850メートル、氷点下10度',
          body: '標高850メートル。地獄谷の冬は厳しく、1メートルを越える雪に覆われ、最低気温が氷点下10度を下回る。',
          source: 'https://jigokudani-yaenkoen.co.jp/about/',
          mapQuery: '地獄谷野猿公苑',
        },
        {
          id: 'jigokudani-life',
          x: 0.55,
          y: 0.62,
          title: '寒さをしのぐ知恵',
          body: '温泉を利用するのは、氷点下10度を下回る冬の地獄谷の厳しい寒さをしのぐために身につけた知恵である。1964年の開苑以来、ニホンザルの生態を間近で観察できる場所として知られ、1970年には米『LIFE』誌の表紙に掲載された。',
          source: 'https://jigokudani-yaenkoen.co.jp/about/',
          mapQuery: '山ノ内町 地獄谷',
        },
        {
          id: 'jigokudani-rules',
          x: 0.8,
          y: 0.8,
          title: '餌をやらない、見つめない',
          body: 'ニホンザルに食べ物は絶対に与えてはいけない。サルは触られたり、おどかされたりして身の危険を感じると、威嚇したり噛み付いたりする。近くで相手の目をじっと見つめることは、敵意の表情になる。',
          source: 'https://jigokudani-yaenkoen.co.jp/caution/',
          mapQuery: '地獄谷 温泉',
        },
      ],
    },
  ],
};
